// Operator console top bar — NovemAI İç Panel shell dili (panel/Shell.jsx
// InternalTopBar portu, B1).
//
// Graphite chrome continues across the top edge (mockup: dark header on
// dark sidebar — the whole frame reads "internal"), serif page-app title
// per the novem type system, INTERNAL badge in the warning ramp.
//
// The INTERNAL badge is the lowest-cost mistake-preventer for spec 60:
// any operator glance immediately confirms "I'm in the control plane,
// not in a customer tenant." The persistent impersonation band sits
// above the bar (ImpersonationBand); the sudo countdown is still open.
// The sign-out control moved to the LeftRail footer per the mockup layout.

import { Menu, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";

import { LanguageSwitcher } from "@novemai/components";

import { useLocalePreference } from "@/i18n/useLocalePreference";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/store/ui";

import { ImpersonationBand } from "./ImpersonationBand";

export function TopBar(): React.ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const { locale, setLocale } = useLocalePreference();
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const toggleMobileNav = useUiStore((s) => s.toggleMobileNav);

  // Preferred OIDC claims first; fall back to `sub` (always present)
  // so the badge never renders "Signed in as " with bare trailing
  // whitespace.
  const userLabel =
    auth.user?.profile.preferred_username ??
    auth.user?.profile.name ??
    auth.user?.profile.sub ??
    "operator";

  return (
    <>
      <ImpersonationBand />
      <header
        className={cn(
          "flex h-14 shrink-0 items-center gap-3 border-b border-[var(--rail-line)]",
          "bg-[var(--bg-inverse)] px-4 text-[var(--fg-on-inverse)] md:px-7",
        )}
        role="banner"
      >
        {/* Dar ekranda kenar çubuğunun TEK açma yolu. Geniş ekranda çubuk zaten
            görünür, düğme oradaki tek işlevini kaybeder. */}
        <button
          type="button"
          onClick={toggleMobileNav}
          aria-expanded={mobileNavOpen}
          aria-label={t("nav.open_menu")}
          className={cn(
            "-ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px]",
            "text-[var(--rail-fg-secondary)] hover:bg-[var(--rail-line)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
            "md:hidden",
          )}
        >
          <Menu size={18} aria-hidden="true" />
        </button>
        {/* `truncate`: başlık `whitespace-nowrap` olduğu için en dar hâli TÜM
            metnin genişliğiydi ve bant onu 375px'e sığdıramıyordu. Kırpma
            başlığı kısaltır, DAHİLİ rozetini değil — rozet spec 60'ın yanlış
            yüzey uyarısı, kaybolduğu an kısalan şey bilgi değil güvenlik. */}
        <span className="truncate font-serif text-[18px] font-medium tracking-[-0.005em]">
          {t("app.title")}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-[6px] border px-2 py-0.5",
            "border-[var(--color-warning-700)] bg-[var(--color-warning-950)]",
            "text-[10px] font-bold uppercase tracking-wider text-[var(--color-warning-300)]",
          )}
          aria-label={t("app.internal_badge")}
        >
          <ShieldAlert size={11} aria-hidden="true" />
          {t("app.internal_badge")}
        </span>
        <span className="hidden font-serif text-[13px] italic text-[var(--rail-fg-muted)] lg:inline">
          {t("app.tagline")}
        </span>

        <span className="flex-1" />

        <LanguageSwitcher
          value={locale}
          onChange={setLocale}
          aria-label={t("app.language_label")}
          getOptionLabel={(language) => t("app.change_language_to", { language })}
        />

        {/* Tablette gizli: kim olduğunu kenar çubuğunun altbilgisi zaten söylüyor
            ve bu kopya 768px'te bandı 4px taşırıyordu. Kırpmak yerine gizlemek,
            çünkü kırpılmış hâli ("opadmin ol…") kimseye bir şey söylemez. */}
        {auth.isAuthenticated && (
          <span className="hidden font-sans text-xs text-[var(--rail-fg-secondary)] lg:inline">
            {t("auth.signed_in_as", { name: userLabel })}
          </span>
        )}
      </header>
    </>
  );
}
